import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { initials, timeAgo } from "@/lib/lifeos";
import {
  X, Sparkles, Loader2, Check, Trash2, Mail, MessageSquare, Phone, Users, Coins,
  CalendarClock, ScrollText, Inbox as InboxIcon, TrendingUp, AlertTriangle, Send, Pencil,
} from "lucide-react";

const TABS = [
  { key: "inbox", label: "Life Inbox", icon: InboxIcon },
  { key: "people", label: "People", icon: Users },
  { key: "opportunities", label: "Revenue Rescue", icon: Coins },
  { key: "commitments", label: "Commitments", icon: CalendarClock },
  { key: "audit", label: "Audit Trail", icon: ScrollText },
];

const KIND_META = {
  draft_reply: { label: "Draft reply", color: "#00f0ff", icon: Mail },
  follow_up: { label: "Follow-up", color: "#ffb454", icon: Send },
  opportunity_nudge: { label: "Revenue rescue", color: "#36f08a", icon: TrendingUp },
  commitment_reminder: { label: "Commitment", color: "#a78bfa", icon: CalendarClock },
};

const HEALTH = { warm: { c: "#36f08a", t: "Warm" }, cooling: { c: "#ffb454", t: "Cooling" }, cold: { c: "#f43f5e", t: "Cold" } };
const STAGE = {
  dormant: { c: "#a78bfa", t: "Dormant" }, at_risk: { c: "#f43f5e", t: "At risk" },
  active: { c: "#36f08a", t: "Active" }, won: { c: "#00f0ff", t: "Won" }, lost: { c: "#71717a", t: "Lost" },
};
const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageSquare, call: Phone, dm: MessageSquare };

const money = (v, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);

function Avatar({ name, color }) {
  return (
    <span className="shrink-0 grid place-items-center rounded-full font-display font-bold text-[11px]"
      style={{ width: 34, height: 34, background: `${color}1a`, border: `1.5px solid ${color}66`, color }}>
      {initials(name)}
    </span>
  );
}

function Stat({ icon: Icon, label, value, color, sub }) {
  return (
    <div data-testid={`life-stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-mono">{label}</span>
      </div>
      <div className="font-display font-black text-2xl leading-none" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1.5 font-mono">{sub}</div>}
    </div>
  );
}

function InboxCard({ item, onApprove, onDismiss }) {
  const meta = KIND_META[item.kind] || KIND_META.draft_reply;
  const Icon = meta.icon;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.draft || "");
  const [busy, setBusy] = useState("");
  const done = item.status !== "pending";

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "approve") { await onApprove(item.id, draft); }
      else { await onDismiss(item.id); }
    } finally { setBusy(""); }
  };

  return (
    <article data-testid="life-inbox-card" className={`rounded-xl border bg-[#09111c] p-4 transition-opacity ${done ? "opacity-50 border-white/5" : "border-white/10"}`}>
      <div className="flex items-start gap-3">
        <Avatar name={item.person?.name || "?"} color={meta.color} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full" style={{ color: meta.color, background: `${meta.color}14`, border: `1px solid ${meta.color}33` }}>
              <Icon size={10} /> {meta.label}
            </span>
            <span className="text-[10px] font-mono text-zinc-500">{item.person?.name}{item.person?.company ? ` · ${item.person.company}` : ""}</span>
            <span className="ml-auto text-[10px] font-mono" style={{ color: meta.color }}>{item.confidence}% conf</span>
          </div>
          <h3 className="text-sm font-semibold text-zinc-100 mt-1.5">{item.title}</h3>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{item.summary}</p>
        </div>
      </div>

      {(item.draft || editing) && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-500">Proposed message</span>
            {!done && (
              <button data-testid="life-inbox-edit" onClick={() => setEditing((e) => !e)} className="text-[10px] font-mono text-zinc-400 hover:text-cyan-300 flex items-center gap-1">
                <Pencil size={10} /> {editing ? "Preview" : "Edit"}
              </button>
            )}
          </div>
          {editing ? (
            <textarea data-testid="life-inbox-draft" value={draft} onChange={(e) => setDraft(e.target.value)} rows={6}
              className="w-full bg-[#05090f] border border-cyan-400/20 rounded-lg p-3 text-[12px] text-zinc-200 leading-relaxed focus:outline-none focus:ring-2 focus:ring-cyan-400/40" />
          ) : (
            <pre className="text-[12px] leading-relaxed text-zinc-300 whitespace-pre-wrap font-sans bg-[#05090f] border border-white/10 rounded-lg p-3 max-h-48 overflow-y-auto thin-scroll">{draft}</pre>
          )}
        </div>
      )}

      {done ? (
        <div data-testid="life-inbox-result" className={`mt-3 text-[11px] font-mono ${item.status === "approved" ? "text-emerald-300" : "text-zinc-500"}`}>
          {item.status === "approved" ? "✓ " : "✕ "}{item.result || item.status}
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2">
          <button data-testid="life-inbox-approve" disabled={!!busy} onClick={() => act("approve")}
            className="inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold uppercase tracking-wider rounded-lg px-3 h-9 shadow-[0_0_18px_rgba(0,240,255,0.3)] transition-all disabled:opacity-60">
            {busy === "approve" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve & send
          </button>
          <button data-testid="life-inbox-dismiss" disabled={!!busy} onClick={() => act("dismiss")}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-red-300 border border-white/10 hover:border-red-400/30 rounded-lg px-3 h-9 transition-colors">
            {busy === "dismiss" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Dismiss
          </button>
        </div>
      )}
    </article>
  );
}

export default function LifeInbox({ onClose }) {
  const [tab, setTab] = useState("inbox");
  const [overview, setOverview] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [people, setPeople] = useState([]);
  const [opps, setOpps] = useState([]);
  const [comms, setComms] = useState([]);
  const [audit, setAudit] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  const load = useCallback(async () => {
    const [o, i, p, op, c, a] = await Promise.all([
      api.lifeOverview(), api.lifeInbox(), api.lifePeople(), api.lifeOpportunities(), api.lifeCommitments(), api.lifeAudit(),
    ]);
    setOverview(o); setInbox(i); setPeople(p); setOpps(op); setComms(c); setAudit(a);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Could not load your Life Graph")); }, [load]);

  const discover = async () => {
    setDiscovering(true);
    toast.loading("Chief of Staff is reviewing your world...", { id: "disc" });
    try {
      const res = await api.lifeDiscover();
      toast.success(res.message, { id: "disc" });
      await load();
      if (res.created > 0) setTab("inbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discovery could not complete", { id: "disc" });
    } finally { setDiscovering(false); }
  };

  const approve = async (id, draft) => {
    try { const r = await api.lifeApprove(id, draft); toast.success(r.result || "Approved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const dismiss = async (id) => {
    try { await api.lifeDismiss(id); toast("Dismissed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not dismiss"); }
  };

  const pending = useMemo(() => inbox.filter((i) => i.status === "pending"), [inbox]);
  const handled = useMemo(() => inbox.filter((i) => i.status !== "pending"), [inbox]);

  return (
    <div className="max-w-5xl mx-auto pt-8 px-4 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2">
            <Sparkles size={18} /> Life Inbox
          </h2>
          <p className="text-xs text-zinc-500 mt-1">Your Chief of Staff manages your world by exception — you only decide what matters.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="life-discover-button" onClick={discover} disabled={discovering}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold uppercase tracking-wider rounded-xl px-4 h-9 shadow-[0_0_20px_rgba(0,240,255,0.35)] transition-all disabled:opacity-60">
            {discovering ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Run discovery
          </button>
          <button data-testid="life-close-button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
        </div>
      </div>

      {/* Simulated banner */}
      <div data-testid="life-simulated-banner" className="flex items-center gap-2 rounded-lg border border-amber-400/25 bg-amber-500/[0.06] px-3 py-2 my-4">
        <AlertTriangle size={13} className="text-amber-300 shrink-0" />
        <p className="text-[11px] text-amber-200/80">This is a <strong>clearly-labeled simulated workspace</strong> demonstrating the full discovery-to-approval flow. Connect Gmail in Connections to run this on your authorized live data.</p>
      </div>

      {/* Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat icon={InboxIcon} label="Awaiting You" value={overview.pending_approvals} color="#00f0ff" sub="in your Life Inbox" />
          <Stat icon={Coins} label="Revenue Rescue" value={money(overview.pipeline_value)} color="#36f08a" sub={`${money(overview.rescue_value)} dormant / at-risk`} />
          <Stat icon={CalendarClock} label="Commitments" value={overview.open_commitments} color="#a78bfa" sub={`${overview.overdue_commitments} overdue`} />
          <Stat icon={Users} label="People" value={overview.people} color="#ffb454" sub={`${overview.open_opportunities} open deals`} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white/[0.03] rounded-xl p-1 border border-white/5 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = t.key === "inbox" ? pending.length : t.key === "people" ? people.length : t.key === "opportunities" ? opps.length : t.key === "commitments" ? comms.length : 0;
          return (
            <button key={t.key} data-testid={`life-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-tactical font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${tab === t.key ? "bg-cyan-500/15 text-cyan-300 border border-cyan-400/30" : "text-zinc-400 hover:text-zinc-200"}`}>
              <Icon size={13} /> {t.label}{count ? <span className="text-[9px] opacity-70">({count})</span> : null}
            </button>
          );
        })}
      </div>

      {/* INBOX */}
      {tab === "inbox" && (
        <div className="space-y-3" data-testid="life-inbox-list">
          {pending.length === 0 && handled.length === 0 && (
            <div className="text-center py-16">
              <Sparkles size={30} className="mx-auto text-cyan-400/40 mb-3" />
              <p className="text-zinc-500 text-sm">Your inbox is clear. Hit <span className="text-cyan-300">Run discovery</span> to let the Chief of Staff scan your world.</p>
            </div>
          )}
          {pending.map((i) => <InboxCard key={i.id} item={i} onApprove={approve} onDismiss={dismiss} />)}
          {handled.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-600 pt-4 pb-1">Handled</div>
              {handled.map((i) => <InboxCard key={i.id} item={i} onApprove={approve} onDismiss={dismiss} />)}
            </>
          )}
        </div>
      )}

      {/* PEOPLE */}
      {tab === "people" && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="life-people-list">
          {people.map((p) => {
            const h = HEALTH[p.health] || HEALTH.warm;
            return (
              <article key={p.id} data-testid="life-person-card" className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
                <Avatar name={p.name} color={h.c} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-zinc-100 truncate">{p.name}</h3>
                    <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded" style={{ color: h.c, background: `${h.c}14` }}>{h.t}</span>
                  </div>
                  <p className="text-[11px] font-mono text-zinc-500 truncate">{p.relationship}{p.company ? ` · ${p.company}` : ""}{p.role ? ` · ${p.role}` : ""}</p>
                  {p.notes && <p className="text-xs text-zinc-400 mt-2 leading-relaxed line-clamp-2">{p.notes}</p>}
                  <div className="flex items-center gap-3 mt-2 text-[10px] font-mono">
                    {p.open_opportunities > 0 && <span className="text-emerald-300">{p.open_opportunities} open deal{p.open_opportunities > 1 ? "s" : ""}</span>}
                    {p.open_commitments > 0 && <span className="text-violet-300">{p.open_commitments} commitment{p.open_commitments > 1 ? "s" : ""}</span>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* OPPORTUNITIES */}
      {tab === "opportunities" && (
        <div className="space-y-3" data-testid="life-opps-list">
          {opps.length === 0 && <div className="text-center text-zinc-600 text-sm py-16">No opportunities yet. Run discovery to surface money left on the table.</div>}
          {opps.map((o) => {
            const s = STAGE[o.stage] || STAGE.dormant;
            return (
              <article key={o.id} data-testid="life-opp-card" className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-zinc-100 truncate">{o.title}</h3>
                      <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded" style={{ color: s.c, background: `${s.c}14` }}>{s.t}</span>
                    </div>
                    <p className="text-[11px] font-mono text-zinc-500">{o.person?.name}{o.person?.company ? ` · ${o.person.company}` : ""}</p>
                    {o.reason && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{o.reason}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-black text-lg text-emerald-300">{money(o.value, o.currency)}</div>
                    <div className="text-[10px] font-mono text-zinc-500">{o.confidence}% conf</div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* COMMITMENTS */}
      {tab === "commitments" && (
        <div className="space-y-2" data-testid="life-comms-list">
          {comms.length === 0 && <div className="text-center text-zinc-600 text-sm py-16">No commitments tracked yet.</div>}
          {comms.map((c) => {
            const overdue = c.due_at && c.status === "open" && new Date(c.due_at) < new Date();
            const dueColor = c.status === "done" ? "#36f08a" : overdue ? "#f43f5e" : "#a78bfa";
            return (
              <article key={c.id} data-testid="life-comm-card" className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 flex items-center gap-3">
                <CalendarClock size={16} style={{ color: dueColor }} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${c.status === "done" ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{c.description}</p>
                  <p className="text-[10px] font-mono text-zinc-500">{c.person?.name || "—"} · {c.kind?.replace(/_/g, " ")}{c.due_at ? ` · due ${new Date(c.due_at).toLocaleDateString()}` : ""}</p>
                </div>
                <span className="text-[9px] uppercase tracking-wider font-mono px-2 py-0.5 rounded shrink-0" style={{ color: dueColor, background: `${dueColor}14` }}>
                  {c.status === "done" ? "Done" : overdue ? "Overdue" : "Open"}
                </span>
              </article>
            );
          })}
        </div>
      )}

      {/* AUDIT */}
      {tab === "audit" && (
        <div className="space-y-1.5" data-testid="life-audit-list">
          {audit.length === 0 && <div className="text-center text-zinc-600 text-sm py-16">No activity yet.</div>}
          {audit.map((a) => (
            <div key={a.id} data-testid="life-audit-row" className="flex items-start gap-3 rounded-lg border border-white/5 bg-white/[0.015] px-3 py-2.5">
              <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ color: a.actor === "You" ? "#00f0ff" : "#a78bfa", background: a.actor === "You" ? "#00f0ff14" : "#a78bfa14" }}>{a.actor}</span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-zinc-200">{a.action}</p>
                {a.detail && <p className="text-[11px] text-zinc-500 leading-relaxed">{a.detail}</p>}
              </div>
              <span className="text-[10px] font-mono text-zinc-600 shrink-0">{timeAgo(a.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
